import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { ButtonHeight } from '~/components/ui/Button';
import { CloseButton } from '~/components/ui/CloseButton';
import { PanelControls } from '~/components/ui/PanelControls';
import { ShopContainer } from '~/components/ui/Shop';
import { ShopBalance } from '~/components/ui/ShopBalance';
import { ShopHead } from '~/components/ui/ShopHead';
import { ShopPreview } from '~/components/ui/ShopPreview';
import { rage, useInputAction, useMouseButton, useObservable } from '~/rage';
import {
	InputAction,
	InputKey,
	KeyPressStates,
	MouseButton,
	VehicleTuningCategory,
	WalletType,
} from '~/shared';
import losSantos from '../../assets/images/los_santos.svg';
import { CategoryTab } from '../../components/CategoryTab';
import { ListTab } from '../../components/ListTab';
import { WheelSettingsTab } from '../../components/WheelSettingsTab';
import { useBuy, useShopPreview } from '../../hooks';
import {
	resetStore,
	setActiveStage,
	setShopItems,
	TuningSlice,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { getEffectiveTuningSelection } from '../../utils/resolveTuningShopVariant';
import {
	StyledContainer,
	StyledControlsContainer,
	StyledRightContainer,
} from './Tuning.styled';

const STAGE_COMPONENTS: Record<TuningSlice['activeStage'], React.FC> = {
	list: ListTab,
	category: CategoryTab,
	// eslint-disable-next-line camelcase
	wheel_settings: WheelSettingsTab,
};

const Tuning: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const items = useObservable(rage.vehicleTuningShop.getShopItems());
	const activeStage = useLayoutSelector((state) => state.tuning.activeStage);
	const activeCategory = useLayoutSelector(
		(state) => state.tuning.activeCategory,
	);
	const activeList = useLayoutSelector((state) => state.tuning.activeList);
	const activeListIndex = useLayoutSelector(
		(state) => state.tuning.activeListIndex,
	);
	const [interfaceHidden, setInterfaceHidden] = useState(false);
	const modalState = useObservable(rage.modal.getCurrentModal());
	const activeVariants = useLayoutSelector(
		(state) => state.tuning.activeVariants,
	);

	const { handleBuy } = useBuy();
	const shopPreview = useShopPreview();

	const handleMouseButton = useCallback((state: KeyPressStates) => {
		if (state === KeyPressStates.Pressed) {
			setInterfaceHidden(true);
		} else if (state === KeyPressStates.Unpressed) {
			setInterfaceHidden(false);
		}
	}, []);

	const handleSelectCategory = useCallback(() => {
		let nextActiveStage: TuningSlice['activeStage'] = 'list';
		if (activeCategory === VehicleTuningCategory.WHEEL_SETTINGS) {
			nextActiveStage = 'wheel_settings';
		}

		dispatch(setActiveStage(nextActiveStage));
	}, [activeCategory]);

	useEffect(() => {
		if (activeStage !== 'category') return;

		rage.vehicleTuningShop
			.removeTemporaryItems()
			.catch((err) => rage.notifications.showException(err));
		rage.vehicleTuningShop
			.resetWheelsSettings()
			.catch((err) => rage.notifications.showException(err));
	}, [activeStage]);

	const leftControls = useMemo(() => {
		if (activeStage === 'category') {
			return [
				{
					text: 'Управление',
					keys: [
						InputKey.ArrowUp,
						InputKey.ArrowDown,
						InputKey.ArrowLeft,
						InputKey.ArrowRight,
					],
				},
				{
					text: 'Выбрать категорию',
					keys: [InputKey.Enter],
					onClick: () => handleSelectCategory(),
				},
				{
					text: 'Крутить транспорт',
					keys: [InputKey.MouseRight],
				},
			];
		}

		if (activeStage === 'list') {
			const activeItem =
				activeListIndex !== null ? activeList[activeListIndex] : null;
			const effective = activeItem
				? getEffectiveTuningSelection(activeItem, activeVariants)
				: null;
			const isInstalled = effective?.isInstalled;

			if (isInstalled) {
				return [
					{
						text: 'Управление',
						keys: [
							InputKey.ArrowUp,
							InputKey.ArrowDown,
							InputKey.ArrowLeft,
							InputKey.ArrowRight,
						],
					},
					{
						text: 'Крутить транспорт',
						keys: [InputKey.MouseRight],
					},
				];
			}

			return [
				{
					text: 'Управление',
					keys: [
						InputKey.ArrowUp,
						InputKey.ArrowDown,
						InputKey.ArrowLeft,
						InputKey.ArrowRight,
					],
				},
				{
					text: 'Купить',
					keys: [InputKey.Enter],
					onClick: () => handleBuy(WalletType.Cash),
				},
				{
					text: 'Крутить транспорт',
					keys: [InputKey.MouseRight],
				},
			];
		}

		if (activeStage === 'wheel_settings') {
			return [
				{
					text: 'Купить',
					keys: [InputKey.Enter],
					onClick: () => handleBuy(WalletType.Cash),
				},
				{
					text: 'Крутить транспорт',
					keys: [InputKey.MouseRight],
				},
			];
		}

		return [];
	}, [
		activeStage,
		activeCategory,
		activeList,
		activeListIndex,
		activeVariants,
		handleBuy,
	]);

	const rightControls = [
		{
			text: 'Выйти',
			keys: ['Esc'],
			onClick: () => rage.vehicleTuningShop.exitMenu(),
		},
	];

	useInputAction(InputAction.CommonMenuClose, () => {
		if (modalState) {
			return;
		}

		if (activeStage === 'list' || activeStage === 'wheel_settings') {
			dispatch(setActiveStage('category'));
			return;
		}

		rage.vehicleTuningShop
			.exitMenu()
			.catch((err) => rage.notifications.showException(err));
	});

	useMouseButton(MouseButton.Right, handleMouseButton, {
		state: KeyPressStates.Both,
	});

	useEffect(() => {
		dispatch(setShopItems(items));
	}, [items]);

	useEffectOnce(() => {
		Promise.all([
			rage.vehicleTuningShop
				.fetchOwnedItemsSettings()
				.catch((err) => rage.notifications.showException(err)),
			rage.vehicleTuningShop
				.fetchOwnedFreeTuning()
				.catch((err) => rage.notifications.showException(err)),
		]);

		return () => {
			rage.vehicleTuningShop
				.removeTemporaryItems()
				.catch((err) => rage.notifications.showException(err));
			rage.vehicleTuningShop
				.resetWheelsSettings()
				.catch((err) => rage.notifications.showException(err));

			dispatch(resetStore());
		};
	});

	const CurrentStage = STAGE_COMPONENTS[activeStage];

	return (
		<StyledContainer onContextMenu={(e) => e.preventDefault()}>
			<StyledRightContainer style={{ opacity: interfaceHidden ? 0 : 1 }}>
				<ShopBalance />
				<CloseButton
					height={ButtonHeight.Height40}
					onClick={() => rage.vehicleTuningShop.exitMenu()}
				/>
			</StyledRightContainer>
			<ShopContainer style={{ opacity: interfaceHidden ? 0 : 1 }}>
				<ShopHead img={losSantos} text={'Тюнинг студия'} height={30} />
				{CurrentStage && <CurrentStage />}
			</ShopContainer>
			<StyledControlsContainer>
				<PanelControls controls={leftControls} align="left" />
				<PanelControls controls={rightControls} align="right" />
			</StyledControlsContainer>
			<ShopPreview
				{...shopPreview}
				visible={shopPreview.visible && !interfaceHidden}
			/>
		</StyledContainer>
	);
};

export { Tuning };
